import { X } from 'lucide-react';
import { useEffect } from 'react';

// Module-level stack so only the topmost open modal responds to Escape.
const _modalStack = [];

/**
 * Shared back-office modal shell — Phase 3.
 * Linear/Stripe-style: white surface, slate-200 border, small radius, soft shadow.
 * Sticky header + footer + scrollable body.
 *
 * Props:
 *  - title (string|node)
 *  - subtitle (optional string|node)
 *  - icon (optional Lucide icon component)
 *  - onClose
 *  - size: 'sm' | 'md' | 'lg' | 'xl'  (default 'md')
 *  - zClass: tailwind z-* override (default 'z-50'). Use 'z-[60]' for nested modals.
 *  - headerActions: optional ReactNode rendered between title & close
 *  - footer: optional ReactNode rendered as sticky footer
 *  - testid: data-testid on the root modal surface
 *  - children: body content
 */
const SIZE = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-2xl',
  lg: 'sm:max-w-3xl',
  xl: 'sm:max-w-5xl',
};

export default function OpsModal({ title, subtitle, icon: Icon, onClose, size = 'md', zClass = 'z-50', headerActions, footer, testid, children }) {
  // Escape closes — only when this modal is on top of the stack.
  useEffect(() => {
    const id = {};
    _modalStack.push(id);
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (_modalStack[_modalStack.length - 1] === id) onClose?.();
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      const idx = _modalStack.indexOf(id);
      if (idx >= 0) _modalStack.splice(idx, 1);
    };
  }, [onClose]);

  return (
    <div className={`ops-shell fixed inset-0 ${zClass} bg-slate-900/40 backdrop-blur-[2px] flex items-end sm:items-center justify-center p-0 sm:p-4`} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        data-testid={testid}
        className={`bg-white w-full ${SIZE[size] || SIZE.md} max-h-[95vh] sm:max-h-[88vh] rounded-t-lg sm:rounded-lg border border-slate-200 shadow-md flex flex-col overflow-hidden`}
      >
        {/* Header */}
        <div className="shrink-0 border-b border-slate-200 px-5 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0 flex items-center gap-2.5">
            {Icon && <Icon size={15} className="text-slate-500 shrink-0" />}
            <div className="min-w-0">
              <h2 className="text-[14px] font-semibold text-slate-900 tracking-tight truncate">{title}</h2>
              {subtitle && <p className="text-[12px] text-slate-500 truncate leading-tight">{subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {headerActions}
            <button
              onClick={onClose}
              data-testid="ops-modal-close"
              className="text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded p-1 transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-white">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="shrink-0 border-t border-slate-200 px-5 py-3 bg-white">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
